import { useEffect, useState } from "react";
import { type TradeInAemDispatchType, useTradeInAemSelector } from "../redux/store";
import { useTradeInCsl } from "../csl/tradeInCslUtils";
import { type TradeInAemNavStepType, getNavStepIndex, getNavStepsConfigs } from "../utils/navUtils";
import { isDevMode } from "../utils/utils";
import { useDispatch } from "../../shared-logic/redux/store";

type NavStepIndexType = {
    start: number;
    end: number;
};

type UseTradeInAemDesktopNavigationItems = {
    activeNavigationSteps: TradeInAemNavStepType[];
    showPreviousArrow: boolean;
    showNextArrow: boolean;
    clickPreviousArrow: () => void;
    clickNextArrow: () => void;
};

const useTradeInAemNavigationItemsDesktop = (): UseTradeInAemDesktopNavigationItems => {
    const dispatch = useDispatch<TradeInAemDispatchType>();
    const currentStep = useTradeInAemSelector((state) => state.tradeInAemNav.step);
    const commonSettings = useTradeInAemSelector((state) => state.commonSettings);
    const csl = useTradeInCsl();
    const stepList = csl.enabledNavSteps;
    const numberOfSteps = stepList.length;
    const newStepInitialIndex = { start: 0, end: numberOfSteps < 4 ? numberOfSteps : 4 };
    const [newStepIndexes, setNavStepIndexes] = useState<NavStepIndexType>(newStepInitialIndex);

    const navStepsConfigs = getNavStepsConfigs(csl, currentStep, stepList, dispatch, commonSettings);
    const { start, end } = newStepIndexes;
    const activeNavigationSteps = navStepsConfigs.slice(start, end);

    const showPreviousArrow = newStepIndexes.start !== 0;
    const showNextArrow = newStepIndexes.end !== numberOfSteps;

    // Move the navigation bar to the right side
    const moveNavigationForward = (amount: number): void => {
        // Can navigate amount of steps to the right side
        if (newStepIndexes.end + amount < numberOfSteps) {
            const newEnd = newStepIndexes.end + amount;
            setNavStepIndexes({ start: newEnd - 4, end: newEnd });
        } else {
            // Fallback to last 4 items
            setNavStepIndexes({ start: numberOfSteps - 4, end: numberOfSteps });
        }
    };

    // Move the navigation bar to the left
    const moveNavigationBackwards = (amount: number): void => {
        // Can navigate amount of steps to the left side
        if (newStepIndexes.start - amount > 0) {
            const newStart = newStepIndexes.start - amount;
            setNavStepIndexes({ start: newStart, end: newStart + 4 });
        } else {
            // Fallback to first 4 items
            setNavStepIndexes(newStepInitialIndex);
        }
    };

    // Move 3 steps to the left side if possible
    const clickPreviousArrow = (): void => moveNavigationBackwards(3);
    // Move 3 steps to the right side if possible
    const clickNextArrow = (): void => moveNavigationForward(3);

    // The newStepIndexes will change automatically so there will always be a next or previous step if possible
    useEffect(() => {
        if (numberOfSteps > 4) {
            const currentIndex = getNavStepIndex(csl, currentStep);
            // If the current step has no previous step move 2 steps to left side if possible
            if (newStepIndexes.start === currentIndex) moveNavigationBackwards(2);

            // If the current step has no next step move 2 steps to right side if possible
            if (newStepIndexes.end === currentIndex + 1) moveNavigationForward(2);
        }
    }, [currentStep]);

    // Update newStepIndexes when currentStep is not 0 on initialize
    // Only used when devMode is enabled for now
    useEffect(() => {
        const currentIndex = getNavStepIndex(csl, currentStep);
        // Move to the step before the current step
        if (isDevMode(commonSettings) && currentIndex !== 0) {
            moveNavigationForward(currentIndex - 1);
        }
    }, []);

    return {
        activeNavigationSteps,
        showPreviousArrow,
        showNextArrow,
        clickPreviousArrow,
        clickNextArrow,
    };
};

export default useTradeInAemNavigationItemsDesktop;
